import {NIL as EmptyUUID, v4 as UUIDv4} from 'uuid';
import type {BaseSyntheticEvent} from "react";
import {FileReadingOptions, ImageNaturalSize, ModifyEventConfig, OperatingSystems} from "./type-declerations";
import {jsonToCSV} from 'react-papaparse'
import type {UnparseConfig} from "papaparse";
import $ from 'jquery';
import {DateUtils, DateUtilsTypes} from "../../../core";
import {format, normalize} from 'phone-formatter';
import {firstValueFrom, Observable} from 'rxjs';


interface DateComparatorOptions {
    unit?: DateUtilsTypes.UnitType;
    type?: | "local" | 'server' | 'utc';
    format?: string;
}

/**
 * The utility methods used in the application.
 */
class Utils {

    /**
     * The currency formatter for the application.
     */
    public static CurrencyFormatter = new Intl.NumberFormat('en-US', {currency: 'CAD', style: 'currency'});

    private static youtubeWidgetId = 1000;

    //              ########################### COMPARATORS ###################################

    /**
     * The empty UUID string.
     */
    static get emptyUUID() {
        return EmptyUUID;
    }

    /**
     * Compares two numbers
     * @param a         the first number
     * @param b         the second number
     * @return          0 if the numbers are equal, -1 if a is less than b, 1 if a is greater than b
     */
    static numComparator(a: number, b: number): number {
        if (a === b) return 0;
        if (a < b) return -1;
        return 1
    }

    /**
     * Compares two dates by converting them to moment objects and then comparing them
     * @param a         the first date
     * @param b         the second date
     * @param options   the options for the date comparison
     * @return          0 if the dates are equal, -1 if a is less than b, 1 if a is greater than b
     */
    static dateComparator(a: DateUtilsTypes.FormatObject, b: DateUtilsTypes.FormatObject, options?: DateComparatorOptions): number {
        const {unit = 'second', type = 'server', format} = options ?? {};
        const _dayjsComparator = (a: DateUtilsTypes.Instance, b: DateUtilsTypes.Instance) => {
            if (a.isSame(b, unit)) return 0;
            if (a.isAfter(b, unit)) return 1;
            return -1;
        }
        if (DateUtils.instance.isDayjs(a) && DateUtils.instance.isDayjs(b)) {
            return _dayjsComparator(a, b);
        }
        switch (type) {
            case 'local':
                return _dayjsComparator(DateUtils.local(a, format), DateUtils.local(b, format));
            case 'server':
                return _dayjsComparator(DateUtils.server(a, format), DateUtils.server(b, format));
            case "utc":
                return _dayjsComparator(DateUtils.UTC(a, format), DateUtils.UTC(b, format));
        }
    }

    /**
     * Compares two strings.
     * @param a             the first string
     * @param b             the second string
     * @return              0 if the strings are equal, -1 if a is less than b, 1 if a is greater than b
     */
    static stringComparator(a: string, b: string): number {
        return a?.localeCompare(b);
    }

    //              ########################### UTILITIES ###################################

    /**
     * Compares two Booleans
     * @param a             the first boolean
     * @param b             the second boolean
     * @return              0 if the booleans are equal, -1 if a is less than b, 1 if a is greater than b
     */
    static booleanComparator(a: boolean, b: boolean): number {
        if (a === b) return 0;
        if (a < b) return -1;
        return 1;
    }

    /**
     * Determines if two objects are equal.
     *
     * @param object1       the first object
     * @param object2       the second object
     * @return              true if the objects are equal, false otherwise
     */
    static deepEqual(object1: any, object2: any): boolean {
        // check if the first one is an array
        if (Array.isArray(object1)) {
            if (!Array.isArray(object2) || object1.length !== object2.length) return false;
            for (let i = 0; i < object1.length; i++) {
                if (!this.deepEqual(object1[i], object2[i])) return false;
            }
            return true;
        }
        // check if the first one is an object
        if (typeof object1 === 'object' && object1 !== null && object2 !== null) {
            if (!(typeof object2 === 'object')) return false;
            const keys = Object.keys(object1);
            if (keys.length !== Object.keys(object2).length) return false;
            for (const key in object1) {
                if (!this.deepEqual(object1[key], object2[key])) return false;
            }
            return true;
        }
        // not array and not object, therefore must be primitive
        return object1 === object2;
    }

    /**
     * Deep copy an acyclic *basic* Javascript object.  T
     *
     * * this method only handles basic scalars (strings, numbers, booleans) and arbitrarily deep arrays and objects
     * containing these.
     * * This method does *not* handle instances of other classes.
     * @template T      the type of the object
     * @param obj       the object to be deep copied
     * @return          the deep copied object
     */
    static deepCopy<T = any>(obj: T): T {
        let ret: any, key;
        let marker = '__deepCopy';

        // @ts-ignore
        if (obj && obj[marker])
            throw (new Error('attempted deep copy of cyclic object'));

        if (obj && obj.constructor === Object) {
            ret = {};
            // @ts-ignore
            obj[marker] = true;

            for (key in obj) {
                if (key === marker)
                    continue;

                // @ts-ignore
                ret[key] = this.deepCopy(obj[key]);
            }

            // @ts-ignore
            delete (obj[marker]);
            return (ret);
        }

        // @ts-ignore
        if (obj && obj.constructor === Array) {
            ret = [];
            // @ts-ignore
            obj[marker] = true;

            // @ts-ignore
            for (key = 0; key < obj.length; key++)
                ret.push(this.deepCopy(obj[key]));

            // @ts-ignore
            delete (obj[marker]);
            return (ret);
        }
        // It must be a primitive type -- just return it.
        return (obj);
    }

    /**
     * Performs a deep merge of objects and returns new object. Does not modify
     * objects (immutable) and merges arrays via concatenation.
     *
     * @param objects           the objects to be merged
     * @returns                 the merged object
     */
    static deepMerge(...objects: Record<string, any>[]): Record<string, any> {
        const isObject = (obj: any) => obj && typeof obj === 'object';

        return objects.reduce((prev: any, obj: any) => {
            for (const key of Object.keys(obj)) {
                const pVal = prev[key];
                const oVal = obj[key];

                if (Array.isArray(pVal) && Array.isArray(oVal)) {
                    prev[key] = [...new Set([...oVal, ...pVal])];
                } else if (isObject(pVal) && isObject(oVal)) {
                    prev[key] = this.deepMerge(pVal, oVal);
                } else {
                    prev[key] = oVal;
                }
            }

            return prev;
        }, {});
    }

    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.arrayBuffer): PromiseLike<ArrayBuffer | null>;

    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.json): PromiseLike<Record<string, any> | null>;

    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.text | FileReadingOptions.dataUrl | FileReadingOptions.binaryString): PromiseLike<string | null>;

    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions): PromiseLike<string | ArrayBuffer | Record<string, any> | null> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            switch (as) {
                case FileReadingOptions.text:
                case FileReadingOptions.json:
                    reader.readAsText(file);
                    break;
                case FileReadingOptions.dataUrl:
                    reader.readAsDataURL(file);
                    break;
                case FileReadingOptions.arrayBuffer:
                    reader.readAsArrayBuffer(file);
                    break;
                case FileReadingOptions.binaryString:
                    reader.readAsBinaryString(file);
                    break;
            }
            reader.onload = () => {
                if (as === FileReadingOptions.json) {
                    try {
                        resolve(JSON.parse(reader.result as string))
                    } catch (e) {
                        reject(e);
                    }
                } else {
                    resolve(reader.result)
                }
            };
            reader.onerror = error => reject(error);
        });
    }

    /**
     * Fetches the natural size of the image associated with the given imageUrl when rendered in the browser.
     * @param imageUrl      the url of the image
     * @return              the natural size of the image
     */
    static getImageSize(imageUrl: string): Promise<ImageNaturalSize | null> {
        return new Promise<ImageNaturalSize | null>((resolve) => {
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                resolve({
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                })
            }
            image.onerror = () => {
                resolve(null)
            }
        })
    }

    /**
     * Creates a Unique Identifier in form of a string
     * @return      the unique identifier
     */
    static createUUId(): string {
        const uuid = UUIDv4();
        return `ID-${uuid}`
    }

    /**
     * Downloads the given blob with the given exported file name.
     * @param blob                  the blob to be downloaded
     * @param exportedFileName      the name of the exported file
     */
    static downloadFile(blob: Blob, exportedFileName: string): void {
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, exportedFileName);
        } else {
            const link = document.createElement("a");
            if (link.download !== undefined) {
                link.href = URL.createObjectURL(blob);
                link.download = exportedFileName;
                link.style.visibility = 'hidden';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }

    /**
     * Exports a .json file from the provided [json] object and the [fileTitle].
     * @param json              the json object to be exported
     * @param fileTitle         the title of the file
     */
    static exportJsonFile(json: Record<string, any>, fileTitle: string): void {
        const exportedFileName = fileTitle + '.json' || 'template.json';
        const text = JSON.stringify(json);
        const blob = new Blob([text], {type: 'data:application/json;charset=utf-8;'});
        this.downloadFile(blob, exportedFileName);
    }

    /**
     * Exports a .csv file from the provided [data] and the [fileTitle].
     * @param data              the data to be exported
     * @param fileTitle         the title of the file
     * @param options           the options for the csv export
     */
    static exportCsvFile(data: any, fileTitle: string, options?: UnparseConfig): void {
        const csv = jsonToCSV(data, {skipEmptyLines: true, ...(options ?? {})});
        const exportedFileName = fileTitle ? fileTitle.toLowerCase().endsWith('.csv') ? fileTitle : `${fileTitle}.csv` : 'export.csv';
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        this.downloadFile(blob, exportedFileName);
    }

    /**
     * Returns the given value with the maximum amount of decimals provided.
     * @param value             the value to be formatted
     * @param maxDecimals       the maximum number of decimals
     * @param returnType        the type of the return value
     * @return                  the formatted value
     */
    static numberWithMaximumDecimals(
        value: string | number,
        maxDecimals: number = 2,
        returnType: "string" | "number" = 'string'
    ): number | string {
        const modulus = Number(value) % 1;
        if (modulus === 0) {
            return value;
        }
        const modulusString = value.toString();

        const numberOfDecimals = modulusString.split('.')?.length > 1
            ? modulusString.split('.')[1].length
            : 0;
        const valueWithCorrectDecimals = typeof value === 'string'
            ? parseFloat(value).toFixed(Math.min(numberOfDecimals, maxDecimals))
            : value.toFixed(Math.min(numberOfDecimals, maxDecimals));

        switch (returnType) {
            case 'string':
                return valueWithCorrectDecimals;
            case "number":
            default:
                return parseFloat(valueWithCorrectDecimals);
        }
    }

    /**
     * Awaits for the specified time in milliseconds.
     * @param milliseconds          the time to wait in milliseconds
     * @return                      a promise that resolves after the specified time
     */
    static async wait(milliseconds: number): Promise<void> {
        await new Promise(r => setTimeout(r, milliseconds));
    }

    /**
     * Modifies the given event with the given options.
     *
     * @param event                 the event to be modified
     * @param config                the configuration for modifying the event
     */
    static modifyEvent(
        event: Event | BaseSyntheticEvent,
        config: ModifyEventConfig = {
            preventDefault: true,
            stopPropagation: true,
            stopImmediatePropagation: false,
        }
    ): void {
        if (config.preventDefault) {
            event.preventDefault();
        }
        if (config.stopPropagation) {
            event.stopPropagation();
        }
        if (config.stopImmediatePropagation && "stopImmediatePropagation" in event) {
            event.stopImmediatePropagation();
        }
    }

    /**
     * Clamps the given value between the min and max numbers.
     *
     * @param value         the value to be clamped
     * @param min           the minimum value
     * @param max           the maximum value
     * @return              the clamped value
     */
    static clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(value, max))
    }

    /**
     * Rounds the given number with a fraction digits between [minimumFractionDigits] and [maximumFractionDigits]
     *
     * @param value                         the value to be rounded
     * @param minimumFractionDigits         the minimum number of fraction digits
     * @param maximumFractionDigits         the maximum number of fraction digits
     * @return                              the rounded value
     */
    static round(
        value: number,
        minimumFractionDigits?: number,
        maximumFractionDigits?: number
    ): number {
        const formattedValue = value.toLocaleString('en',
            {
                useGrouping: false,
                minimumFractionDigits,
                maximumFractionDigits
            }
        )
        return Number(formattedValue)
    }

    /**
     * Sets the application's title and description from given values.
     *
     * @param title             the title of the application
     * @param description       the description of the application
     */
    static setAppInfo({title, description}: { title?: string, description?: string } = {}): void {
        if (title) {
            document.title = title;
        }
        if (description) {
            $('meta[name="description"]').attr("content", description);
        }
    }

    /**
     * Deeply removes all the instances of the provided property from the given entity.
     *
     * @param property      the name of the property to be removed
     * @param entity        the entity to remove the property from
     * @return              the entity with the property removed
     */
    static deepRemoveProperty<T>(property: keyof T, entity: T): Omit<T, typeof property> {
        if (!entity)
            return entity;
        if (Array.isArray(entity)) {
            return entity.map(e => this.deepRemoveProperty(property, e)) as T;
        }
        if (typeof entity === 'object') {
            for (const key of Object.keys(entity)) {
                if (key === property) {
                    delete entity[key as keyof T];
                } else {
                    const value = entity[key as keyof T];
                    const innerProp = property as keyof typeof value;
                    entity[key as keyof T] = this.deepRemoveProperty(innerProp, entity[key as keyof T]) as typeof value;
                }
            }
        }
        return entity;
    }

    /**
     * Title cases a given string.
     * @param string        the string to be title cased
     * @return              the title cased string
     */
    static stringToTitleCase(string: string): string {
        return string[0].toUpperCase().concat(
            string
                .slice(1)
                .replaceAll(/[A-Z]/g, (char) => ` ${char}`)
                .replaceAll(/[A-Za-z][0-9]/g, (char) => `${char[0]} ${char[1]}`)
        );
    }

    /**
     * Corrects the url of the video by forcing it to start with https
     * @param url      the url to be corrected
     * @return         the corrected url
     */
    static correctUrl(url: string): string {
        if (!url?.length) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `https://${url}`;
    }

    /**
     * Determines if the given url is from YouTube.
     * @param url       the url to be checked
     * @return          true if the url is from YouTube, false otherwise
     */
    static isYoutubeUrl(url: string): boolean {
        return !!this.getYoutubeVideoId(url);
    }

    /**
     * Fetches the YouTube video id from the YouTube url.
     * @param url       the YouTube url
     * @return          the YouTube video id
     */
    static getYoutubeVideoId(url: string): string | undefined {
        const regExp = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
        const urlMatch = url.match(regExp);
        return urlMatch?.[1];
    }

    /**
     * Fetches the data of the YouTube video with the given id.
     * @param youtubeId     the id of the YouTube video
     * @return              the data of the YouTube video
     */
    static getYoutubeVideoData(youtubeId: string) {
        return firstValueFrom<any>(new Observable((observer) => {
            const widgetId = ++this.youtubeWidgetId;

            let embed = document.createElement('iframe');
            embed.setAttribute('src', `https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&widgetid=${widgetId}`);
            embed.style.cssText = "position: absolute; display: hidden";
            embed.onload = function () {
                const message = JSON.stringify({event: 'listening', id: widgetId, channel: 'widget'});
                embed.contentWindow?.postMessage(message, 'https://www.youtube.com');
            }

            /**
             * Parses the data from the YouTube video.
             * @param e
             */
            const parseData = (e: MessageEvent) => {
                try {
                    const {event, id, info} = JSON.parse(e.data)
                    if (event === 'initialDelivery' && id === widgetId) {
                        observer.next(info.videoData)
                    }
                } catch {
                    if (typeof e.data === 'object') {
                        const {event, id, info} = e.data;
                        if (event === 'initialDelivery' && id === widgetId) {
                            observer.next(info.videoData)
                        }
                    }
                }
            }

            document.body.appendChild(embed);
            window.addEventListener("message", parseData)
            return () => {
                window.removeEventListener("message", parseData)
                document.body.removeChild(embed)
            }
        }));
    }

    /**
     * Fetches the information about the os of the user.
     */
    static getOsSpecs() {
        const os = this.getOS();
        const isMac = os === OperatingSystems.mac;
        const isIOS = os === OperatingSystems.ios;
        const isWindows = os === OperatingSystems.windows;
        const isAndroid = os === OperatingSystems.android;
        const isLinux = os === OperatingSystems.linux;
        return {
            os,
            isMac,
            isIOS,
            isWindows,
            isAndroid,
            isLinux,
        };
    }

    /**
     * Takes a screenshot of the users' browser window.
     *
     * * first uses the mediaDevices of the navigator to take the screenshot, if erroneous, creates the screenshot
     * using the canvas approach.
     * @param allowExplicitScreenshot       if true, allows the user to take a screenshot explicitly
     * @return                              the base64 encoded string of the screenshot
     */
    static async screenshotUserBrowserWindow(allowExplicitScreenshot = false): Promise<string | null> {
        try {
            if (!allowExplicitScreenshot) {
                // throw so that the screenshot is handled with a canvas
                // noinspection ExceptionCaughtLocallyJS
                throw Error("");
            }
            // media display with user permission screenshot.
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            const video = document.createElement("video");
            if (!context) {
                // throw so that the screenshot is handled with a canvas
                // noinspection ExceptionCaughtLocallyJS
                throw Error("");
            }

            const captureStream = await navigator.mediaDevices?.getDisplayMedia({video: false});
            video.srcObject = captureStream;
            context.drawImage(video, 0, 0, window.innerWidth, window.innerHeight);
            captureStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            return this.extractCanvasContentBytes(canvas);
        } catch (err) {
            // canvas screenshot
            return new Promise((resolve) => {
                import('html2canvas').then(async (html2canvas) => {
                    debugger;
                    const canvas = await html2canvas.default(document.body);
                    if (!canvas) {
                        return resolve(null);
                    }
                    return resolve(this.extractCanvasContentBytes(canvas));
                }).catch((e) => {
                    resolve(null);
                })
            })
        }
    }

    /**
     * Creates the content bytes of the image obtained from the given canvas.
     * @param canvas                the canvas to be converted to content bytes
     * @return                      the base64 encoded string of the image
     */
    static async extractCanvasContentBytes(canvas: HTMLCanvasElement): Promise<string | null> {
        const toBlob = (): Promise<Blob | null> => new Promise(r => canvas.toBlob(r));
        const blob = await toBlob();
        if (!blob) {
            return null;
        }
        const binaryString = await this.readFile(blob, FileReadingOptions.binaryString)
        if (!binaryString) {
            return null;
        }
        return btoa(binaryString);
    }

    /**
     * Copies the given text to the clipboard.
     *
     * @param text    the text to be copied to the clipboard
     * @return        true if the text was copied successfully, false otherwise
     */
    static copyToClipboard(text: string): Promise<boolean> {
        return new Promise((res) => {
            if (!navigator.clipboard) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    res(true)
                } catch (err) {
                    console.error('Failed to copy text: ', err);
                    res(false)
                }
                document.body.removeChild(textArea);
            } else {
                navigator.clipboard.writeText(text)
                    .then(() => {
                        res(true)
                    })
                    .catch(err => {
                        console.error('Failed to copy text: ', err);
                        res(false)
                    });
            }
        })
    }

    /**
     * Formats the given phone number.
     *
     * @param number    The phone number to format.
     * @param options   The options for formatting the phone number.
     */
    static formatPhoneNumber(number: string, options?: { normalize?: boolean, pattern?: string, withoutAreaCode?: boolean, areaCode?: string }) {
        const imputedOptions = {
            normalize: true,
            pattern: '+N (NNN) NNN-NNNN',
            withoutAreaCode: false,
            areaCode: '1',
            ...(options ?? {}),
        }
        if (!number) {
            return '--';
        }
        if (imputedOptions.normalize) {
            number = normalize(number);
        }
        if (!imputedOptions.withoutAreaCode && imputedOptions.areaCode) {
            number = imputedOptions.areaCode.toString() + number;
        }
        return format(number, imputedOptions.pattern, {normalize: true});
    }

    /**
     * Reorders the entities based on the new order index of the entity to move.
     *
     * @param list              The list of entities to reorder.
     * @param oldIndex          The old index of the entity to move.
     * @param newIndex          The new index of the entity to move.
     * @param orderIndexKey     The key of the order index property of the entity.
     */
    static reorderList<T extends object>(list: T[], oldIndex: number, newIndex: number, orderIndexKey?: keyof T): T[] {
        let entities = Array.from(list);

        if (oldIndex < 0 || oldIndex >= entities.length || newIndex < 0 || newIndex >= entities.length) {
            throw new Error("Invalid indices");
        }

        const [movedElement] = entities.splice(oldIndex, 1); // Remove the element from the old index
        entities.splice(newIndex, 0, movedElement); // Insert the element at the new index

        if (orderIndexKey) {
            entities = entities.map((entity, index) => {
                entity[orderIndexKey] = index as T[keyof T]
                return entity;
            });
        }

        return entities;
    }

    /**
     * Download a URL as a file.
     * @param url           The URL to download.
     * @param filename      The filename to save the file as.
     */
    static downloadUrl(url: string, filename: string): void {
        const element = document.createElement('a');
        element.setAttribute('href', url);
        element.setAttribute('download', filename);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    /**
     * Formats the bytes into a human-readable format.
     * @param bytes        The bytes to format.
     * @returns            The formatted bytes.
     */
    static formatBytes(bytes: number): string {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Fetches the OS of the system with 90% accuracy.
     *
     * @author Vladyslav Turak
     * @see https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
     */
    private static getOS() {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
        const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        let os = null;

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.mac;
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.ios;
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.windows;
        } else if (/Android/.test(userAgent)) {
            os = OperatingSystems.android;
        } else if (/Linux/.test(platform)) {
            os = OperatingSystems.linux;
        }
        return os;
    }
}

export * from './type-declerations';
export default Utils;
